import { useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useDestinations,
  useCreateDestination,
  useUpdateDestination,
  useDeleteDestination,
} from '@/hooks/queries/useDestinations'
import { DestinationsToolbar } from '@/pages/destinations/components/DestinationsToolbar'
import type { Destination, DestinationFormData } from '@/types'

type DialogState =
  | { mode: 'none' }
  | { mode: 'create' }
  | { mode: 'edit'; destination: Destination }
  | { mode: 'delete'; destination: Destination }

const EMPTY_FORM: DestinationFormData = {
  name: '',
  description: '',
  address: '',
  is_collection_point: false,
  is_valorizer: false,
}

type DestinationFilter = 'collection_point' | 'valorizer'

export function DestinationsPage() {
  const { data: destinations, isLoading } = useDestinations()
  const createMutation = useCreateDestination()
  const updateMutation = useUpdateDestination()
  const deleteMutation = useDeleteDestination()

  const [dialog, setDialog] = useState<DialogState>({ mode: 'none' })
  const [form, setForm] = useState<DestinationFormData>(EMPTY_FORM)
  const [search, setSearch] = useState('')
  const [selectedTypes, setSelectedTypes] = useState<DestinationFilter[]>([])

  const openCreate = () => {
    setForm(EMPTY_FORM)
    createMutation.reset()
    setDialog({ mode: 'create' })
  }

  const openEdit = (destination: Destination) => {
    setForm({
      name: destination.name,
      description: destination.description ?? '',
      address: destination.address ?? '',
      is_collection_point: destination.is_collection_point,
      is_valorizer: destination.is_valorizer,
    })
    updateMutation.reset()
    setDialog({ mode: 'edit', destination })
  }

  const openDelete = (destination: Destination) => {
    deleteMutation.reset()
    setDialog({ mode: 'delete', destination })
  }

  const closeDialog = () => setDialog({ mode: 'none' })

  const handleSubmit = async () => {
    const payload: DestinationFormData = {
      ...form,
      description: form.description || '',
      address: form.address || '',
    }
    if (dialog.mode === 'create') {
      await createMutation.mutateAsync(payload)
      closeDialog()
    } else if (dialog.mode === 'edit') {
      await updateMutation.mutateAsync({ id: dialog.destination.id, data: payload })
      closeDialog()
    }
  }

  const handleDelete = async () => {
    if (dialog.mode !== 'delete') return
    await deleteMutation.mutateAsync(dialog.destination.id)
    closeDialog()
  }

  const clearFilters = () => {
    setSearch('')
    setSelectedTypes([])
  }

  const isFormDialog = dialog.mode === 'create' || dialog.mode === 'edit'
  const activeMutation = dialog.mode === 'create' ? createMutation : updateMutation
  const formFieldErrors = isFormDialog
    ? ((activeMutation.error as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors ?? {})
    : {}
  const formError = isFormDialog
    ? ((activeMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? null)
    : null
  const deleteError = dialog.mode === 'delete'
    ? ((deleteMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Ocurrió un error inesperado')
    : null

  const nameError = !form.name.trim() ? 'El nombre es obligatorio.' : formFieldErrors.name?.[0]
  const typeError =
    !form.is_collection_point && !form.is_valorizer
      ? 'Selecciona al menos un tipo.'
      : formFieldErrors.is_collection_point?.[0] ?? formFieldErrors.is_valorizer?.[0]
  const descriptionError = formFieldErrors.description?.[0]
  const addressError = formFieldErrors.address?.[0]
  const isFormValid = Boolean(form.name.trim() && (form.is_collection_point || form.is_valorizer))
  const hasActiveFilters = Boolean(search.trim() || selectedTypes.length)
  const normalizedSearch = search.trim().toLowerCase()
  const filteredDestinations = (destinations ?? []).filter((destination) => {
    const matchesType =
      !selectedTypes.length ||
      selectedTypes.some((type) =>
        type === 'collection_point'
          ? destination.is_collection_point
          : destination.is_valorizer
      )

    if (!matchesType) return false
    if (!normalizedSearch) return true

    const searchableText = [
      destination.name,
      destination.description ?? '',
      destination.address ?? '',
      destination.is_collection_point ? 'centro de acopio acopio' : '',
      destination.is_valorizer ? 'valorizador valorizacion valorización' : '',
    ]
      .join(' ')
      .toLowerCase()

    return searchableText.includes(normalizedSearch)
  })

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Disposiciones finales</h1>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo destino
        </Button>
      </div>

      <Card>
        <CardHeader className="space-y-4">
          <DestinationsToolbar
            search={search}
            onSearchChange={setSearch}
            selectedTypes={selectedTypes}
            onSelectedTypesChange={setSelectedTypes}
            onClearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
          />
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Cargando...</p>
          ) : !destinations?.length ? (
            <p className="text-sm text-muted-foreground">No hay destinos registrados.</p>
          ) : !filteredDestinations.length ? (
            <p className="text-sm text-muted-foreground">
              No se encontraron destinos con los filtros seleccionados.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
                    <th className="pb-2">Nombre</th>
                    <th className="pb-2">Tipo</th>
                    <th className="pb-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDestinations.map((d) => (
                    <tr key={d.id} className="border-b last:border-0">
                      <td className="py-3 font-medium text-foreground">{d.name}</td>
                      <td className="py-3">
                        <div className="flex gap-1">
                          {d.is_collection_point && (
                            <span className="inline-block rounded-full bg-[#7c8c43]/20 px-2 py-0.5 text-xs font-medium text-[#383f2d]">
                              Acopio
                            </span>
                          )}
                          {d.is_valorizer && (
                            <span className="inline-block rounded-full bg-primary/25 px-2 py-0.5 text-xs font-medium text-secondary">
                              Valorizador
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="mr-1 h-8 w-8"
                          onClick={() => openEdit(d)}
                          aria-label={`Editar ${d.name}`}
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openDelete(d)}
                          aria-label={`Eliminar ${d.name}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={isFormDialog} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog.mode === 'create' ? 'Nuevo destino' : 'Editar destino'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="dest-name">Nombre *</Label>
              <Input
                id="dest-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                autoFocus
              />
              {nameError && <p className="text-sm text-red-600">{nameError}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="dest-description">Descripción</Label>
              <Textarea
                id="dest-description"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
              {descriptionError && <p className="text-sm text-red-600">{descriptionError}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="dest-address">Dirección</Label>
              <Input
                id="dest-address"
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              />
              {addressError && <p className="text-sm text-red-600">{addressError}</p>}
            </div>
            <div className="space-y-2">
              <Label>Tipo de destino *</Label>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_collection_point}
                    onChange={(e) => setForm((f) => ({ ...f, is_collection_point: e.target.checked }))}
                    className="h-4 w-4 rounded border-border accent-[#7c8c43]"
                  />
                  Centro de acopio
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_valorizer}
                    onChange={(e) => setForm((f) => ({ ...f, is_valorizer: e.target.checked }))}
                    className="h-4 w-4 rounded border-border accent-[#7c8c43]"
                  />
                  Valorizador
                </label>
              </div>
              {typeError && <p className="text-sm text-red-600">{typeError}</p>}
            </div>
            {formError && !Object.keys(formFieldErrors).length && (
              <p className="text-sm text-red-600">{formError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || activeMutation.isPending}
            >
              {activeMutation.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={dialog.mode === 'delete'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar destino</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/70">
            ¿Estás seguro que deseas eliminar{' '}
            <span className="font-semibold">
              {dialog.mode === 'delete' ? dialog.destination.name : ''}
            </span>
            ?
          </p>
          {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
